import { BaseElement } from 'base';
import { bus } from 'bus';
import { SIZE_GUIDE_TOPIC } from 'size-guide-topic';

const HEADER_TAG = 'drawer-header';

const FOCUSABLE = [
  "a[href]:not([tabindex='-1'])",
  'button:not([disabled])',
  'input:not([disabled])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  "[tabindex]:not([tabindex='-1'])"
]
  .map(selector => `[data-size-guide-panel] ${selector}`)
  .join(', ');

class SizeGuide extends BaseElement {
  #dialog = null;
  #panel = null;
  #body = null;
  #content = null;
  #shown = null;
  #open = false;
  #opener = null;
  #unit = 'cm';

  onConnect() {
    this.#dialog = this.q('[data-size-guide-dialog]');
    this.#panel = this.q('[data-size-guide-panel]');
    this.#body = this.q('[data-size-guide-body]');
    this.#content = this.q('[data-size-guide-content]');
    if (!this.#dialog || !this.#panel || !this.#content) {
      console.error('[size-guide] missing dialog, panel or content:', this);
      return;
    }

    this.listen(this.#dialog, 'cancel', this.#onCancel);
    this.listen(this.#dialog, 'close', this.#onDialogClose);
    this.listen(this, 'keydown', this.#onKeydown);
    this.listen(this, 'click', this.#onClick);
    this.listen(this, 'scroll', this.#onScroll, {
      capture: true,
      passive: true
    });
    this.observe(new ResizeObserver(() => this.#syncIndicator()), this.#panel);

    customElements.whenDefined(HEADER_TAG).then(() => {
      if (!this.isConnected) return;
      this.q(HEADER_TAG)?.run(() => this.requestDismiss());
    });

    this.subscribe(SIZE_GUIDE_TOPIC, this.#onActive);
  }

  onDisconnect() {
    if (this.#dialog?.open) this.#dialog.close();
    this.#open = false;
    this.#shown = null;
    this.#opener = null;
  }

  requestDismiss() {
    bus.publish(SIZE_GUIDE_TOPIC, null);
  }

  #onActive(value) {
    if (!value) {
      this.#close();
      return;
    }
    if (value === this.#shown) {
      this.#show();
      return;
    }

    this.#shown = value;
    this.#unit = 'cm';
    this.#content.innerHTML = value.html ?? '';
    if (this.#open) this.#resetScroll();
    this.#show();
  }

  #resetScroll() {
    this.#panel.scrollTop = 0;
    if (this.#body) this.#body.scrollTop = 0;
  }

  #show() {
    if (this.#open) {
      this.#afterRender();
      return;
    }

    this.#open = true;
    const active = document.activeElement;
    if (active && active.localName !== 'body' && !this.contains(active))
      this.#opener = active;

    if (this.#dialog.open && !this.#dialog.matches(':modal'))
      this.#dialog.close();
    if (!this.#dialog.open) this.#dialog.showModal();
    this.#resetScroll();
    this.#panel.focus({ preventScroll: true });

    this.#afterRender();
  }

  #afterRender() {
    requestAnimationFrame(() => this.#syncIndicator());
  }

  #close() {
    if (!this.#open) {
      this.#shown = null;
      return;
    }

    this.#open = false;
    if (this.#dialog.open) this.#dialog.close();
    this.#content.replaceChildren();

    this.#shown = null;

    const opener = this.#opener;
    this.#opener = null;
    if (opener?.isConnected) opener.focus?.();
  }

  #onCancel(event) {
    event.preventDefault();
    this.requestDismiss();
  }

  #onDialogClose() {
    if (this.#open && !this.#dialog.open) this.requestDismiss();
  }

  #onKeydown(event) {
    if (event.key === 'Escape') {
      event.stopPropagation();
      return;
    }
    if (event.key !== 'Tab') return;
    event.stopPropagation();

    const focusable = this.qa(FOCUSABLE).filter(
      element => element.offsetParent !== null
    );
    if (focusable.length === 0) return;

    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    const active = document.activeElement;

    if (event.shiftKey) {
      if (active === first || !focusable.includes(active)) {
        event.preventDefault();
        last.focus();
      }
    } else if (active === last || !focusable.includes(active)) {
      event.preventDefault();
      first.focus();
    }
  }

  #onClick(event) {
    if (event.target.closest('[data-size-guide-scrim]')) {
      this.requestDismiss();
      return;
    }
    if (event.target.closest('[data-size-guide-unit]')) this.#toggleUnit();
  }

  #onScroll(event) {
    if (!event.target.matches?.('[data-size-guide-scroller]')) return;
    this.#syncIndicator();
  }

  #toggleUnit() {
    this.#unit = this.#unit === 'cm' ? 'in' : 'cm';
    this.qa('tr[data-unit]').forEach(row => {
      row.hidden = row.dataset.unit !== this.#unit;
    });
    this.q('[data-size-guide-unit]')?.setAttribute(
      'aria-checked',
      String(this.#unit === 'in')
    );
    this.#syncIndicator();
  }

  #syncIndicator() {
    const block = this.q('[data-size-guide-table]');
    const scroller = this.q('[data-size-guide-scroller]');
    const indicator = this.q('[data-size-guide-indicator]');
    if (!block || !scroller || !indicator) return;

    const { scrollWidth, clientWidth, scrollLeft } = scroller;
    const range = scrollWidth - clientWidth;
    const overflow = range > 1;

    block.toggleAttribute('data-overflow', overflow);
    if (overflow) scroller.setAttribute('tabindex', '0');
    else scroller.removeAttribute('tabindex');

    indicator.style.setProperty(
      '--size-guide-thumb-ratio',
      String(scrollWidth > 0 ? clientWidth / scrollWidth : 1)
    );
    indicator.style.setProperty(
      '--size-guide-thumb-progress',
      String(overflow ? Math.min(Math.max(scrollLeft / range, 0), 1) : 0)
    );
  }
}

if (!customElements.get('size-guide'))
  customElements.define('size-guide', SizeGuide);
